import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GridLayout,
  clearStoredLayout,
  defaultLayout,
  isDefaultLayout,
  moveColumn,
  readStoredLayout,
  resetColumnWidth,
  resizeColumn,
  sameLayout,
  usableLayout,
} from './gridLayout';
import { fetchSavedQuotationGridLayout, saveQuotationGridLayout } from '../../lib/userPreferencesApi';

/**
 * The quotation items grid's column layout: what is on screen, what this user
 * has saved, and the actions between them (ClickUp 86d478wd1; saved per user on
 * the owner's request, 2026-09-17).
 *
 * Extracted from QuotationDetailPage when the Save button was added — the page
 * is a god file (TECH_DEBT #26), so the slice being touched comes out and the
 * page keeps a delegating call.
 *
 *   - Every quotation OPENS on the layout this user saved. Until the server has
 *     answered it shows the layout the old per-browser behaviour remembered (or
 *     the shipped one), so nobody who arranged their columns before this loses
 *     them on the day it ships.
 *   - Dragging and resizing change THIS PAGE only. Nothing is remembered until
 *     Save, which is what makes "always show my saved layout" true.
 *   - Reset columns puts the shipped layout on screen; Save after it to make
 *     that the saved layout too.
 *   - `canSave` is true when the screen differs from the saved layout, so the
 *     button never offers to save what is already saved. If the saved layout
 *     could not be loaded, it is offered once the screen differs from the
 *     shipped layout — saving is how that person gets a layout that loads.
 */
export interface QuotationGridLayoutState {
  layout: GridLayout;
  moveColumn: (fromDisplay: number, toDisplay: number) => void;
  resizeColumn: (canonical: number, px: number) => void;
  resetColumnWidth: (canonical: number) => void;
  resetLayout: () => void;
  saveLayout: () => Promise<void>;
  /** Reset is offered once anything differs from the shipped layout. */
  canReset: boolean;
  /** Save is offered once the screen differs from what this user has saved. */
  canSave: boolean;
  saving: boolean;
  /** Why the last Save failed, in words for a person; null when it did not. */
  saveError: string | null;
  /** Set when the saved layout could not be loaded, so the screen can say so. */
  loadFailed: boolean;
  /** True from a successful Save until the next change, so the Save is acknowledged. */
  justSaved: boolean;
}

/** What a failed Save says. Switched on the code, never on the server's prose (invariant 6). */
export function gridLayoutSaveErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 'GRID_LAYOUT_INVALID') {
    return 'These columns could not be saved. Reset columns, arrange them again and save.';
  }
  if (code === 'PERMISSION_DENIED') {
    return 'You do not have permission to save column layouts.';
  }
  return 'Your columns were not saved. Try again.';
}

export function useQuotationGridLayout(): QuotationGridLayoutState {
  const [layout, setLayout] = useState<GridLayout>(() => readStoredLayout());
  // `undefined` until the server answers; `null` when this user has saved none.
  const [saved, setSaved] = useState<GridLayout | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // Set by the first drag, resize or reset. A saved layout that arrives after the
  // user has already started arranging columns must not yank them back.
  const touchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchSavedQuotationGridLayout()
      .then((stored) => {
        if (cancelled) return;
        // A layout saved before a column was added or removed no longer fits and
        // is treated as none: the grid opens on what it would have without it,
        // and Save stores one that fits.
        const usable = usableLayout(stored);
        setSaved(usable);
        if (usable && !touchedRef.current) setLayout(usable);
      })
      .catch((err) => {
        if (cancelled) return;
        // The grid is fully usable on the layout it opened with, so this is a
        // notice on the toolbar and not a blocking error. `saved` stays unknown;
        // see `canSave` for when Save is still offered.
        // eslint-disable-next-line no-console
        console.warn('[quotation grid] could not load the saved column layout', err);
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Each operation returns the SAME object when nothing changes, so a no-op drag
  // does not re-render or count as a change.
  const handleMoveColumn = useCallback((fromDisplay: number, toDisplay: number) => {
    touchedRef.current = true;
    setJustSaved(false);
    setLayout((current) => moveColumn(current, fromDisplay, toDisplay));
  }, []);

  const handleResizeColumn = useCallback((canonical: number, px: number) => {
    touchedRef.current = true;
    setJustSaved(false);
    setLayout((current) => resizeColumn(current, canonical, px));
  }, []);

  const handleResetColumnWidth = useCallback((canonical: number) => {
    touchedRef.current = true;
    setJustSaved(false);
    setLayout((current) => resetColumnWidth(current, canonical));
  }, []);

  const handleResetLayout = useCallback(() => {
    touchedRef.current = true;
    setJustSaved(false);
    // The legacy per-browser copy would otherwise put these columns back as the
    // starting layout of the next quotation opened before anything is saved.
    clearStoredLayout();
    setLayout(defaultLayout());
  }, []);

  const handleSaveLayout = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setJustSaved(false);
    try {
      const stored = await saveQuotationGridLayout(layout);
      setSaved(usableLayout(stored) ?? layout);
      setLoadFailed(false);
      setJustSaved(true);
      // Saved on the account now; the per-browser copy has nothing left to say.
      clearStoredLayout();
    } catch (err) {
      setSaveError(gridLayoutSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [layout]);

  // A failed load leaves `saved` unknown; Save is still offered once the screen
  // differs from the shipped layout, because saving is exactly how that person
  // gets a layout that loads.
  const canSave =
    saved === undefined
      ? loadFailed && !isDefaultLayout(layout)
      : !sameLayout(layout, saved ?? defaultLayout());

  return {
    layout,
    moveColumn: handleMoveColumn,
    resizeColumn: handleResizeColumn,
    resetColumnWidth: handleResetColumnWidth,
    resetLayout: handleResetLayout,
    saveLayout: handleSaveLayout,
    canReset: !isDefaultLayout(layout),
    canSave,
    saving,
    saveError,
    loadFailed,
    justSaved,
  };
}
